import { useParams } from '@tanstack/react-router';

import { selectUserRoles } from '@iconik-io/shared-lib/src/redux/auth/selectors';
import {
  selectCurrentVersionId,
  selectIsLatestVersion,
} from '@iconik-io/shared-lib/src/redux/entities/selectors/versions';
import { Button, Icon } from '@iconik-io/uad';

import {
  AI_METADATA_MODAL,
  AI_METADATA_REVIEW_MODAL,
} from '../../constants/modals';
import { getAiMetadataGeneration } from '../../ducks/ai_metadata/selectors';
import { segmentTrackEvent } from '../../ducks/analytics_tracking/thunks';
import { openModal } from '../../ducks/modals_manager/slice';
import { getIsAiMetadataEnrichmentEnabled } from '../../ducks/settings/select_features';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';

import styles from './ai_metadata_button.module.css';

type AiMetadataButtonProps = {
  itemId: string;
};

export default function AiMetadataButton({ itemId }: AiMetadataButtonProps) {
  const dispatch = useAppDispatch();
  const enabled = useAppSelector(getIsAiMetadataEnrichmentEnabled);
  const { versionId: routeVersionId } = useParams({ strict: false });

  const userRoles = useAppSelector(selectUserRoles);
  const asset = useAppSelector((store) => store.entities.assets[itemId]);
  const versionId = useAppSelector((state) =>
    selectCurrentVersionId(state, itemId, routeVersionId),
  );
  const isLatestVersion = useAppSelector((state) =>
    selectIsLatestVersion(state, itemId, routeVersionId),
  );
  const generation = useAppSelector((state) =>
    getAiMetadataGeneration(state, itemId, versionId),
  );

  if (!enabled || !asset || !userRoles.can_trigger_metadata_ai_filling) {
    return null;
  }

  const isPending = generation?.status === 'pending';
  const hasProposal =
    generation?.status === 'success' &&
    generation.response?.version_id === versionId;

  if (!isPending && !hasProposal && !isLatestVersion) {
    return null;
  }

  const handleClick = () => {
    dispatch(
      segmentTrackEvent({
        eventName: hasProposal
          ? 'Opened Review AI-Generated Metadata Modal'
          : 'Opened Get AI-Generated Metadata Modal',
        properties: {
          Source: hasProposal ? 'asset page' : 'metadata panel',
        },
      }),
    );
    dispatch(
      openModal({
        id: hasProposal ? AI_METADATA_REVIEW_MODAL : AI_METADATA_MODAL,
        contextItems: [asset],
        options: { versionId },
      }),
    );
  };

  const label = isPending
    ? 'Generating metadata…'
    : hasProposal
      ? 'Review AI-generated metadata'
      : 'Generate metadata with AI';

  return (
    <Button
      className={styles.button}
      variant="secondary"
      fullWidth
      icon={<Icon name="ai_metadata" />}
      onClick={handleClick}
      isDisabled={isPending || !versionId}
    >
      {label}
    </Button>
  );
}
